import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useConsent } from '@/hooks/useConsent'
import { useState } from 'react'
import { CookieConsentDialog } from './CookieConsentDialog'
import { isNative } from '@/lib/platform'
import { useLocation } from 'react-router-dom'
import type { CookiePreferences } from '@/types/consent'

export function ConsentBanner() {
  const { t } = useTranslation()
  const { hasConsent, isLoading, giveConsent, revokeConsent } = useConsent()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const location = useLocation()

  // Handle saving cookie preferences
  const handleSavePreferences = (preferences: CookiePreferences) => {
    if (preferences.analytics) {
      giveConsent()
      return
    }
    revokeConsent()
  }

  // Skip cookie consent on native iOS/Android apps
  if (isNative()) {
    return null
  }

  // List of paths where we don't want to show the consent banner
  const hiddenPaths = [
    '/chats/new',
    '/chats',
    '/giftcards',
    '/giftcard',
    '/book',
    '/booking',
  ]

  const shouldHideBanner = hiddenPaths.some(path => 
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  ) || (location.pathname.match(/^\/chats\/new/) && location.search.includes('source=bio_link'))

  if (shouldHideBanner || hasConsent !== null || isLoading) {
    return null
  }

  return (
    <>
      <div className="fixed bottom-[calc(var(--min-side-padding)+var(--bottom-nav-height)+0.75rem)] left-0 right-0 z-[60]">
        <div className="phone-container-padded">
          <div className="bg-background/90 backdrop-blur-sm rounded-[var(--card-radius)] shadow-lg border border-border/50">
            <div className="flex items-center gap-4 p-4">
              <span className="text-sm text-foreground leading-snug flex flex-col whitespace-nowrap">
                <span className="whitespace-nowrap">THIS SITE</span>
                <span className="whitespace-nowrap">USES COOKIES</span>
              </span>
              <div className="flex gap-2 ml-auto">
                <Button
                  size="sm"
                  onClick={() => handleSavePreferences({ analytics: true })}
                  className="h-8 px-3"
                >
                  {t('consent.accept', 'ACCEPT ALL')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsDialogOpen(true)}
                  className="h-8 px-3 bg-muted/50 hover:bg-muted text-foreground"
                >
                  {t('consent.select', 'SELECT')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CookieConsentDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSavePreferences={handleSavePreferences}
      />
    </>
  )
} 
